import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import MoreVertIcon from '@mui/icons-material/MoreVert'

type AlertRow = {
  id: string
  name: string
  gender: string
  age: string
  vitals: string
  value: string
  occurrences: string
  datetime: string
  status: 'Pending' | 'Processing' | 'Attended'
}

const SAMPLE: AlertRow[] = [
  { id: '1', name: 'Jane Cooper', gender: 'Female', age: '47 yrs', vitals: 'Heart Rate', value: '125 bpm', occurrences: '3/1 week', datetime: '12 Jan 2023, 06:52 PM', status: 'Pending' },
  { id: '2', name: 'Samual Clark', gender: 'Male', age: '57 yrs', vitals: 'Heart Rate', value: '140 bpm', occurrences: '1/24 hrs', datetime: '12 Jan 2023, 04:20 PM', status: 'Pending' },
  { id: '3', name: 'Phillip Warren', gender: 'Male', age: '45 yrs', vitals: 'Blood Pressure', value: '135/96 mmHg', occurrences: '1/24 hrs', datetime: '12 Jan 2023, 02:45 PM', status: 'Pending' },
  { id: '4', name: 'Joseph Jones', gender: 'Male', age: '62 yrs', vitals: 'SPO₂', value: '92%', occurrences: '1/24 hrs', datetime: '12 Jan 2023, 11:55 AM', status: 'Pending' },
  { id: '5', name: 'Ester Fisher', gender: 'Female', age: '38 yrs', vitals: 'SPO₂', value: '91%', occurrences: '1/24 hrs', datetime: '12 Jan 2023, 09:15 AM', status: 'Processing' },
  { id: '6', name: 'Martha Wilson', gender: 'Female', age: '71 yrs', vitals: 'Blood Pressure', value: '140/101 mmHg', occurrences: '3/1 week', datetime: '12 Jan 2023, 06:52 AM', status: 'Attended' },
]

export default function Alerts(): React.ReactElement {
  const [query, setQuery] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const filtered = SAMPLE.filter(r => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return (
      r.name.toLowerCase().includes(q) ||
      r.vitals.toLowerCase().includes(q) ||
      r.value.toLowerCase().includes(q)
    )
  })

  const statusColor = (s: AlertRow['status']) => {
    if (s === 'Pending') return 'warning'
    if (s === 'Processing') return 'info'
    return 'success'
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5">Alerts</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, px: 1 }}>
            <SearchIcon fontSize="small" color="action" />
            <input
              placeholder="Search patient.."
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ border: 'none', outline: 'none', marginLeft: 8, minWidth: 180, background: 'transparent' }}
            />
          </Box>
          <IconButton size="small" aria-label="filter">
            <FilterListIcon />
          </IconButton>
          <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, px: 1 }}>
            <select value="all" onChange={() => {}} style={{ border: 'none', background: 'transparent', padding: '6px 8px' }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="attended">Attended</option>
            </select>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">List</Typography>
            <Typography variant="body2" color="text.secondary">Total Devices : 07</Typography>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Vitals</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Occurrences/Timespan</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.gender}</TableCell>
                  <TableCell>{row.age}</TableCell>
                  <TableCell>{row.vitals}</TableCell>
                  <TableCell>{row.value}</TableCell>
                  <TableCell>{row.occurrences}</TableCell>
                  <TableCell>{row.datetime}</TableCell>
                  <TableCell>
                    <Chip label={row.status} color={statusColor(row.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={e => handleOpenMenu(e)} aria-label="actions">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
            <MenuItem onClick={handleCloseMenu}>View</MenuItem>
            <MenuItem onClick={handleCloseMenu}>Acknowledge</MenuItem>
            <MenuItem onClick={handleCloseMenu}>Assign</MenuItem>
          </Menu>
        </Paper>
      </Box>
    </Box>
  )
}
